/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme';
import { messageService } from '../services/messageService';
import { 
  ArrowLeft, 
  Send, 
  User, 
  Clock, 
  ShieldCheck,
  MessageCircle,
  AlertCircle
} from 'lucide-react';
import { ROUTES } from '../routes';

interface Message {
  id: number;
  senderId: number;
  receiverId: number;
  subject: string;
  body: string;
  createdAt: string;
}

export default function MessageThread() {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const [message, setMessage] = useState<Message | null>(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const currentUserId = Number(localStorage.getItem('userId'));

  useEffect(() => {
    const fetchMessage = async () => {
      try {
        const response = await messageService.getMessageThread(id!);
        setMessage(response.data.data);
      } catch (err) {
        setError('Message not found or you do not have permission to view it.');
      } finally {
        setLoading(false);
      }
    };
    fetchMessage();
  }, [id, navigate]);

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message || !reply.trim()) return;

    setSending(true);
    const token = localStorage.getItem('token');
    const receiverId = message.senderId === currentUserId ? message.receiverId : message.senderId;

    try {
      await messageService.sendMessage({
        receiverId,
        subject: message.subject ? (message.subject.startsWith('Re:') ? message.subject : `Re: ${message.subject}`) : 'Inquiry Reply',
        body: reply
      });
      setReply('');
      // In a real app we might refetch or append to local state
      alert('Reply sent successfully!');
      navigate(ROUTES.MESSAGES);
    } catch (err) {
      alert('Failed to send reply. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.colors.primary }}></div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="max-w-2xl mx-auto mt-20 p-8 rounded-3xl border text-center space-y-4" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
        <AlertCircle size={48} className="mx-auto" style={{ color: theme.colors.danger }} />
        <h2 className="text-2xl font-black" style={{ color: theme.colors.text }}>Conversation Not Found</h2>
        <p style={{ color: theme.colors.mutedText }}>{error || "The message you are looking for doesn't exist or you don't have access."}</p>
        <button onClick={() => navigate(ROUTES.MESSAGES)} className="font-bold flex items-center gap-2 mx-auto transition-all hover:opacity-70" style={{ color: theme.colors.primary }}>
          <ArrowLeft size={18} /> Back to Inbox
        </button>
      </div>
    );
  }

  const isSentByMe = message.senderId === currentUserId;
  const otherPartyLabel = isSentByMe ? `User #${message.receiverId}` : `User #${message.senderId}`;

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate(ROUTES.MESSAGES)}
          className="p-2 rounded-xl border hover:bg-gray-50 transition-all flex items-center gap-2 font-bold text-sm"
          style={{ borderColor: theme.colors.border, color: theme.colors.mutedText }}
        >
          <ArrowLeft size={18} />
          Back to Inbox
        </button>
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest opacity-40">
          <ShieldCheck size={14} className="text-emerald-500" />
          Secure Conversation
        </div>
      </div>

      <div className="p-8 rounded-3xl border shadow-sm space-y-6" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
        <div className="border-b pb-6" style={{ borderColor: theme.colors.border }}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black mb-1" style={{ color: theme.colors.text }}>
                {message.subject || 'Item Inquiry'}
              </h1>
              <div className="flex items-center gap-4 text-sm font-bold" style={{ color: theme.colors.mutedText }}>
                <span className="flex items-center gap-1.5">
                  <User size={16} /> {otherPartyLabel}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock size={16} /> {new Date(message.createdAt).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="py-4">
          <div className={`flex ${isSentByMe ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] p-6 rounded-2xl shadow-sm border ${
              isSentByMe 
                ? 'bg-blue-500 text-white rounded-tr-none border-blue-600' 
                : 'rounded-tl-none'
            }`} style={!isSentByMe ? { backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text } : {}}>
              <p className="whitespace-pre-wrap leading-relaxed">{message.body}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 rounded-3xl border shadow-xl space-y-6" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle size={20} style={{ color: theme.colors.primary }} />
          <h2 className="text-lg font-black" style={{ color: theme.colors.text }}>Send a Reply</h2>
        </div>
        <form onSubmit={handleReply} className="space-y-4">
          <textarea
            required
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={`Message ${otherPartyLabel}...`}
            className="w-full border rounded-2xl p-4 h-40 focus:outline-none focus:ring-4 transition-all resize-none"
            style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              className="px-10 py-4 rounded-2xl font-black text-white transition-all hover:opacity-90 shadow-xl flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: theme.colors.primary }}
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Send size={20} />
              )}
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>

      <div className="p-6 rounded-2xl bg-amber-50 border border-amber-100 flex gap-4 items-start">
        <AlertCircle size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-amber-800 leading-relaxed">
          <p className="font-bold mb-1">Safety First!</p>
          <p>Always meet in a public, well-lit place for transactions. Never share sensitive personal information or financial details in messages.</p>
        </div>
      </div>
    </div>
  );
}
